/**
 * [C28] Browser side of the platform logs (D-259). One trace id per screen visit: every API call made while the
 * screen is open carries it (`x-faylasuf-trace`, with the screen name), and every browser error of that screen
 * is reported with it — so the console finds the screen, its requests and its errors under one id.
 *
 * Reported: unhandled errors, unhandled promise rejections, crashed screens (error boundary) and failed requests
 * (no response, or a 5xx). Business refusals (4xx) are not errors. Texts are redacted here, before they leave the
 * browser (the server redacts again). At most 20 reports a minute, identical ones once per 30 s, sent in small
 * batches; the report call itself is never reported (no loops). Off in fixture mode.
 */
import { CLIENT_ERROR_KINDS, newTraceId, redactPath, redactStack, redactText, shortTrace, type ClientErrorReport } from "@faylasuf/observability/browser";

type Kind = (typeof CLIENT_ERROR_KINDS)[number];

export const APP_VERSION: string = (import.meta.env["VITE_APP_VERSION"] as string | undefined) ?? "dev";
const ENDPOINT = "/api/office/v1/telemetry/errors";

let enabled = false;
let screen = "boot";
let traceId = newTraceId();
const queue: ClientErrorReport[] = [];
const recent = new Map<string, number>();
let sentThisMinute = 0;
let minuteStart = Date.now();
let timer: ReturnType<typeof setTimeout> | null = null;

export function setTelemetryEnabled(on: boolean): void {
  enabled = on;
}

/** A new screen visit: new trace id. Called synchronously when the user navigates, before the screen's requests. */
export function beginScreen(name: string): void {
  screen = name.replace(/[^a-z0-9_./-]/gi, "_").toLowerCase().slice(0, 64) || "screen";
  traceId = newTraceId();
}

export function currentTrace(): { traceId: string; screen: string } {
  return { traceId, screen };
}

/** The short reference shown beside an unexpected error ("رقم التتبع"). */
export function traceRef(id?: string): string {
  return shortTrace(id ?? traceId);
}

function errorParts(err: unknown): { name: string; message: string; stack?: string } {
  if (err instanceof Error) return { name: err.name || "Error", message: err.message, ...(err.stack ? { stack: err.stack } : {}) };
  if (typeof err === "string") return { name: "Error", message: err };
  return { name: "NonError", message: Object.prototype.toString.call(err) };
}

export function reportError(kind: Kind, err: unknown, extra: { status?: number; path?: string; componentStack?: string; traceId?: string } = {}): void {
  if (!enabled) return;
  const p = errorParts(err);
  const stack = redactStack([p.stack, extra.componentStack ? `component stack:${extra.componentStack}` : ""].filter(Boolean).join("\n"));
  const report: ClientErrorReport = {
    kind,
    traceId: extra.traceId ?? traceId,
    screen,
    errorName: p.name.slice(0, 100),
    message: redactText(p.message, 1000) || "(no message)",
    ...(stack ? { stack } : {}),
    ...(typeof extra.status === "number" ? { status: extra.status } : {}),
    ...(extra.path ? { path: redactPath(extra.path) ?? "" } : {}),
    appVersion: APP_VERSION,
  };
  const key = `${kind}|${report.errorName}|${report.message}|${report.path ?? ""}`;
  const now = Date.now();
  if ((recent.get(key) ?? 0) > now - 30_000) return;
  recent.set(key, now);
  if (recent.size > 200) recent.clear(); // review R19: a long-open tab never grows this without bound
  if (now - minuteStart > 60_000) {
    minuteStart = now;
    sentThisMinute = 0;
  }
  if (sentThisMinute >= 20) return;
  sentThisMinute += 1;
  queue.push(report);
  if (queue.length >= 10) void flush();
  else if (!timer) timer = setTimeout(() => void flush(), 2000);
}

export async function flush(): Promise<void> {
  if (timer) clearTimeout(timer);
  timer = null;
  while (queue.length > 0) {
    const reports = queue.splice(0, 10);
    try {
      // keepalive always: a report in flight survives a reload or navigation (found in the C28 browser run)
      await fetch(ENDPOINT, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ reports }), credentials: "same-origin", keepalive: true });
    } catch {
      return; // the network is down: these reports are lost, never retried in a loop
    }
  }
}

let installed = false;
/** window error / unhandledrejection, and a last flush when the page is hidden. */
export function installGlobalHandlers(): void {
  if (installed) return;
  installed = true;
  window.addEventListener("error", (ev) => reportError("client_error", ev.error ?? ev.message));
  window.addEventListener("unhandledrejection", (ev) => reportError("unhandled_rejection", ev.reason));
  window.addEventListener("pagehide", () => void flush());
}

export const TELEMETRY_ENDPOINT = ENDPOINT;
